if (!customElements.get('image-text-heading-block-v2')) {
  customElements.define('image-text-heading-block-v2',
    class ImageTextHeadingBlock extends HTMLElement {
      constructor() {
        super();
        this.swiper = null;
        this.defaultDesktopSrcs = [];
        this.defaultMobileSrcs = [];
        this.variantImagesById = null;
        this.lastAppliedVariantId = null;
        this.missingVariantIdAttempts = 0;
        this.variantSyncTimer = null;
        this.selectedVariantObserver = null;
      }

      connectedCallback() {
        this.initSwiper();
        this.handleResize = this.handleResize.bind(this);
        window.addEventListener('resize', this.handleResize);

        this.onRenderSectionComplete = this.onRenderSectionComplete.bind(this);
        document.addEventListener('renderSection:complete', this.onRenderSectionComplete);

        this.captureDefaultImages();
        this.variantImagesById = this.parseVariantImagesById();

        // Important: on PDP variant changes, `product-info` updates the selected variant script
        // during its render cycle. If this element mounts before that, reading variant id can be
        // temporarily null/old and we would "flash" defaults. Defer first sync by a frame.
        this.updateFromCurrentVariantSelection = this.updateFromCurrentVariantSelection.bind(this);
        requestAnimationFrame(() => {
          this.updateFromCurrentVariantSelection();
        });

        this.setupSelectedVariantObserver();
      }

      disconnectedCallback() {
        window.removeEventListener('resize', this.handleResize);
        this.destroySwiper();
        document.removeEventListener('renderSection:complete', this.onRenderSectionComplete);
        this.clearVariantSyncTimer();
        this.teardownSelectedVariantObserver();
      }

      initSwiper() {
        if (!(window.innerWidth <= 1024) || this.swiper !== null || !window.Swiper) {
          return;
        }

        const swiperContainer = this.querySelector('.swiper-container');
        if (!swiperContainer) {
          return;
        }

        const slides = this.querySelectorAll('.swiper-slide');
        if (slides.length === 0) {
          return;
        }

        const showMobileNavigation = (swiperContainer.closest('.image-text-heading-block')?.dataset.showMobileNavigation === 'true');

        const swiperOptions = {
          slidesPerView: 1, // Strictly show one slide per view
          centeredSlides: false, // Ensure slides are not centered
          initialSlide: 0,
          speed: 800,
          autoHeight: false,
          updateOnWindowResize: true,
          pagination: {
            el: this.querySelector('.image-text-heading-block__mobile-slider .swiper-pagination'),
            clickable: true,
            bulletClass: 'swiper-pagination-bullet',
            bulletActiveClass: 'swiper-pagination-bullet-active',
            renderBullet(index, className) {
              return `<span class="${className}" aria-label="Slide ${index + 1}"></span>`;
            },
            type: 'bullets',
          },
          navigation: {
            nextEl: '.swiper-button-next',
            prevEl: '.swiper-button-prev',
          },
          loop: true,
          allowSlidePrev: true,
          grabCursor: true,
          touchEventsTarget: 'container',
          touchRatio: 1,
          longSwipesRatio: 0.5,
          followFinger: true,
          a11y: {
            enabled: true,
            prevSlideMessage: 'Previous slide',
            nextSlideMessage: 'Next slide',
            firstSlideMessage: 'This is the first slide',
            lastSlideMessage: 'This is the last slide',
            paginationBulletMessage: 'Go to slide {{index}}',
          },
        };

        this.swiper = new window.Swiper(swiperContainer, swiperOptions);

        const setEqualHeights = () => {
          const allSlides = this.querySelectorAll('.swiper-slide');
          if (allSlides.length <= 1) return;

          allSlides.forEach(slide => {
            slide.style.height = 'auto';
          });

          let maxHeight = 0;
          allSlides.forEach(slide => {
            const slideHeight = slide.offsetHeight;
            maxHeight = Math.max(maxHeight, slideHeight);
          });

          if (maxHeight > 0) {
            allSlides.forEach(slide => {
              slide.style.height = `${maxHeight}px`;
            });
          }
        };

        this.swiper.on('init', () => {
          setTimeout(setEqualHeights, 100);
          this.swiper.update();
        });

        this.querySelectorAll('.swiper-slide img').forEach(img => {
          img.addEventListener('load', () => {
            setEqualHeights();
            this.swiper.update();
          });
        });

        if (!showMobileNavigation) {
          const navButtons = this.querySelectorAll('.swiper-button-prev, .swiper-button-next');
          navButtons.forEach((button) => {
            button.style.display = 'none';
          });
        }

        const statusElement = this.querySelector('.current-slide-status');

        this.swiper.on('slideChange', () => {
          const activeIndex = this.swiper.activeIndex;
          const totalSlides = this.swiper.slides.length;

          if (statusElement) {
            statusElement.textContent = `Slide ${activeIndex + 1} of ${totalSlides}`;
          }
        });
      }

      destroySwiper() {
        if (!this.swiper) {
          return;
        }

        this.swiper.destroy(true, true);
        this.swiper = null;
      }

      captureDefaultImages() {
        const scopedRoot = this;
        const desktopImages = scopedRoot.querySelectorAll('.image-text-heading-block__desktop .card_item .image-text-heading-block__image');
        const mobileImages = scopedRoot.querySelectorAll('.image-text-heading-block__mobile-slider .image-text-heading-block__image');
        this.defaultDesktopSrcs = Array.from(desktopImages).map((img) => img.dataset.defaultSrc || img.src);
        this.defaultMobileSrcs = Array.from(mobileImages).map((img) => img.dataset.defaultSrc || img.src);
      }

      handleResize() {
        if (window.innerWidth <= 1023) {
          if (!this.swiper) {
            this.initSwiper();
          }
        } else {
          this.destroySwiper();
        }
      }
      isVariantImagesEnabled() {
        const sectionRoot = this.querySelector('.image-text-heading-block') || this;
        return sectionRoot?.dataset?.showImagesByVariantLevel === 'true';
      }

      parseVariantImagesById() {
        const script = this.querySelector('script[data-feel-the-results-variant-images]');
        if (!script) return null;

        try {
          const parsed = JSON.parse(script.textContent || '{}');
          return parsed && typeof parsed === 'object' ? parsed : null;
        } catch (e) {
          return null;
        }
      }

      clearVariantSyncTimer() {
        if (this.variantSyncTimer) {
          clearTimeout(this.variantSyncTimer);
          this.variantSyncTimer = null;
        }
      }

      scheduleVariantSync(delayMs = 0) {
        this.clearVariantSyncTimer();
        this.variantSyncTimer = setTimeout(() => {
          this.updateFromCurrentVariantSelection();
        }, delayMs);
      }

      setupSelectedVariantObserver() {
        this.teardownSelectedVariantObserver();
        const productInfo = document.querySelector('product-info');
        if (!productInfo || !window.MutationObserver) return;

        // Observe changes to `script[data-selected-variant]` so we can update images as soon
        // as the selected variant JSON is updated (often earlier than any other signals).
        this.selectedVariantObserver = new MutationObserver(() => {
          if (!this.isVariantImagesEnabled()) return;
          this.scheduleVariantSync(0);
        });

        this.selectedVariantObserver.observe(productInfo, {
          subtree: true,
          childList: true,
          characterData: true,
        });
      }

      teardownSelectedVariantObserver() {
        if (this.selectedVariantObserver) {
          this.selectedVariantObserver.disconnect();
          this.selectedVariantObserver = null;
        }
      }

      getCurrentVariantId() {
        const productInfo = document.querySelector('product-info');

        // 1) Preferred: selected variant JSON injected by `product-info`
        const variantScript = productInfo?.querySelector('script[data-selected-variant]');
        if (variantScript?.textContent) {
          try {
            const variant = JSON.parse(variantScript.textContent);
            if (variant?.id) return String(variant.id);
          } catch (e) {}
        }

        // 2) Fallback: URL param
        try {
          const url = new URL(window.location.href);
          const variantId = url.searchParams.get('variant');
          if (variantId) return String(variantId);
        } catch (e) {}

        return null;
      }

      normalizeImagesArray(images) {
        if (!Array.isArray(images) || images.length === 0) return [];

        // Accept either [{src, alt, width, height}, ...] (metafield JSON) or ["url1", ...] (legacy)
        if (typeof images[0] === 'string') {
          return images
            .map((src) => ({ src }))
            .filter((img) => typeof img?.src === 'string' && img.src);
        }

        return images
          .map((img) => (img && typeof img === 'object' ? img : null))
          .filter((img) => typeof img?.src === 'string' && img.src);
      }

      getImagesForVariantId(variantId) {
        if (!variantId) return [];

        const map = this.variantImagesById || this.parseVariantImagesById();
        if (map && typeof map === 'object') {
          return this.normalizeImagesArray(map[String(variantId)]);
        }

        return [];
      }

      updateImages(images) {
        const normalizedImages = this.normalizeImagesArray(images);
        const hasVariantImages = Array.isArray(normalizedImages) && normalizedImages.length > 0;
        const scopedRoot = this;
        const desktopImages = scopedRoot.querySelectorAll('.image-text-heading-block__desktop .card_item .image-text-heading-block__image');
        desktopImages.forEach((img, index) => {
          if (hasVariantImages) {
            const variantIndex = Number.parseInt(img.dataset.variantImageIndex, 10);
            const mappedIndex = Number.isFinite(variantIndex) ? (variantIndex % normalizedImages.length) : (index % normalizedImages.length);
            const next = normalizedImages[mappedIndex];
            if (next?.src) img.src = next.src;
            if (typeof next?.alt === 'string') img.alt = next.alt;
            if (typeof next?.width === 'number') img.width = next.width;
            if (typeof next?.height === 'number') img.height = next.height;
          } else if (this.defaultDesktopSrcs.length) {
            const defaultSrc = img.dataset.defaultSrc || this.defaultDesktopSrcs[index % this.defaultDesktopSrcs.length];
            if (defaultSrc) img.src = defaultSrc;
          }
        });
        const mobileImages = scopedRoot.querySelectorAll('.image-text-heading-block__mobile-slider .image-text-heading-block__image');
        mobileImages.forEach((img, index) => {
          if (hasVariantImages) {
            const variantIndex = Number.parseInt(img.dataset.variantImageIndex, 10);
            const mappedIndex = Number.isFinite(variantIndex) ? (variantIndex % normalizedImages.length) : (index % normalizedImages.length);
            const next = normalizedImages[mappedIndex];
            if (next?.src) img.src = next.src;
            if (typeof next?.alt === 'string') img.alt = next.alt;
            if (typeof next?.width === 'number') img.width = next.width;
            if (typeof next?.height === 'number') img.height = next.height;
          } else if (this.defaultMobileSrcs.length) {
            const defaultSrc = img.dataset.defaultSrc || this.defaultMobileSrcs[index % this.defaultMobileSrcs.length];
            if (defaultSrc) img.src = defaultSrc;
          }
        });
        if (this.swiper) {
          setTimeout(() => {
            this.swiper.updateSlides?.();
            this.swiper.update?.();
          }, 100);
        }
      }
      updateFromCurrentVariantSelection() {
        if (!this.isVariantImagesEnabled()) return;
        const variantId = this.getCurrentVariantId();
        if (!variantId) {
          // During option changes, selected variant JSON / URL can temporarily be unavailable.
          // Don't leave the previous variant's images hanging indefinitely: retry a few times,
          // then fall back to the section's default/customizer images.
          if (this.missingVariantIdAttempts < 3) {
            this.missingVariantIdAttempts += 1;
            this.scheduleVariantSync(60 * this.missingVariantIdAttempts);
            return;
          }

          this.updateImages([]);
          this.lastAppliedVariantId = null;
          return;
        }

        this.missingVariantIdAttempts = 0;
        if (this.lastAppliedVariantId === variantId) return;
        const images = this.getImagesForVariantId(variantId);
        this.updateImages(images);
        this.lastAppliedVariantId = variantId;
      }

      onRenderSectionComplete(event) {
        if (!this.isVariantImagesEnabled()) return;
        const variantId = event?.detail?.variant?.id ? String(event.detail.variant.id) : null;
        if (!variantId) {
          this.missingVariantIdAttempts = 0;
          this.updateImages([]);
          this.lastAppliedVariantId = null;
          return;
        }

        this.missingVariantIdAttempts = 0;
        const images = this.getImagesForVariantId(variantId);
        this.updateImages(images);
        this.lastAppliedVariantId = variantId;

        // In some flows `product-info` can be replaced; re-bind observer defensively.
        this.setupSelectedVariantObserver();
      }
    }
  );
}
